'use client';

/**
 * CasesTable
 *
 * Renders the structured payload returned by `case_management_list_cases`
 * AND `case_management_list_loans` — both tool names route here because
 * cases ARE loans on this platform (see CLAUDE.md §2b service ownership).
 * The column set matches the LoansTable shape from the issue brief, so
 * the same renderer covers both tool names without duplicating components.
 *
 * Issue #1432 Phase 2 — Wave 1 P0
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { z } from '@ops-matrix/shared-types';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { fontFamilies } from '../../theme/typography';
import type { DataTableColumn } from '../../types/dataTable';
import { DataTable } from '../DataTable/DataTable';
import { GlassChip } from '../GlassChip';
import { RendererEmptyState } from './RendererEmptyState';
import { formatStatusLabel, getStatusColor } from './statusColor';
import type { AstorToolRendererProps } from './types';

// ============================================================================
// Schema (lenient — tools may return slightly different shapes)
// ============================================================================

const CaseSchema = z.object({
  id: z.string(),
  caseNumber: z.string().optional(),
  borrowerName: z.string().optional(),
  amount: z.number().optional(),
  interestRate: z.number().optional(),
  status: z.string().optional(),
  propertyAddress: z.string().optional(),
});

type CaseRow = z.infer<typeof CaseSchema>;

const CasesDataSchema = z.object({
  cases: z.array(CaseSchema),
  total: z.number().optional(),
});

const CasesArraySchema = z.array(CaseSchema);

/**
 * Parse `data` into a list of cases. Be permissive — `{ cases: [...] }`,
 * a bare array, and a single-case object are all accepted. Returns null
 * when the payload is unrecognisable.
 */
function parseCasesData(data: unknown): CaseRow[] | null {
  const wrapped = CasesDataSchema.safeParse(data);
  if (wrapped.success) return wrapped.data.cases;

  const bareArray = CasesArraySchema.safeParse(data);
  if (bareArray.success) return bareArray.data;

  const single = CaseSchema.safeParse(data);
  if (single.success) return [single.data];

  return null;
}

// ============================================================================
// UUID detection — render the loan number monospaced when it looks like a UUID
// (CLAUDE.md §11a2: reuse Zod, don't hand-roll a regex).
// ============================================================================

const UuidStringSchema = z.string().uuid();

const isUuid = (str: string): boolean => UuidStringSchema.safeParse(str).success;

// ============================================================================
// Component
// ============================================================================

export const CasesTable = ({ data, onNavigate }: AstorToolRendererProps): ReactElement => {
  const { t, formatCurrency } = useI18n();
  const rows = useMemo(() => parseCasesData(data), [data]);

  const columns: DataTableColumn<CaseRow>[] = useMemo(
    () => [
      {
        id: 'caseNumber',
        header: t('astorTools.cases.caseNumber'),
        accessor: 'caseNumber',
        cell: (value) => {
          const str = value == null ? '' : String(value);
          if (!str) return '';
          return (
            <Typography
              variant="body2"
              component="span"
              sx={{
                fontFamily: isUuid(str) ? fontFamilies.mono : 'inherit',
              }}
            >
              {str}
            </Typography>
          );
        },
      },
      {
        id: 'borrowerName',
        header: t('astorTools.cases.borrower'),
        accessor: 'borrowerName',
      },
      {
        id: 'amount',
        header: t('astorTools.cases.amount'),
        accessor: 'amount',
        align: 'right',
        sortable: true,
        cell: (value) => (typeof value === 'number' ? formatCurrency(value) : ''),
      },
      {
        id: 'interestRate',
        header: t('astorTools.cases.rate'),
        accessor: 'interestRate',
        align: 'right',
        sortable: true,
        cell: (value) => (typeof value === 'number' ? `${value.toFixed(2)}%` : ''),
      },
      {
        id: 'status',
        header: t('astorTools.cases.status'),
        accessor: 'status',
        sortable: true,
        cell: (value) => {
          const status = typeof value === 'string' ? value : '';
          if (!status) return '';
          return (
            <GlassChip
              label={formatStatusLabel(status)}
              color={getStatusColor(status)}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        id: 'propertyAddress',
        header: t('astorTools.cases.property'),
        accessor: 'propertyAddress',
        cell: (value) => {
          const str = value == null ? '' : String(value);
          return (
            <Typography
              variant="body2"
              component="span"
              sx={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 280,
              }}
            >
              {str}
            </Typography>
          );
        },
      },
    ],
    [t, formatCurrency],
  );

  // Parse failure → render parse-error EmptyState. By the time we reach the
  // renderer, MessageBubble has already gated on `pending` and `data ===
  // undefined`, so a non-null `data` that fails the schema is a genuinely
  // malformed final payload — not a streaming intermediate. Showing a
  // forever-skeleton would mask the bug; the EmptyState surfaces it.
  if (rows === null) {
    return (
      <RendererEmptyState
        title={t('astorTools.error.parseFailed')}
        description={t('astorTools.error.parseFailedDescription')}
      />
    );
  }

  // Empty result → friendly empty state.
  if (rows.length === 0) {
    return (
      <RendererEmptyState title={t('astorTools.cases.empty')} description={t('astorTools.cases.emptyDescription')} />
    );
  }

  return (
    <Box sx={{ my: 1.5 }}>
      <DataTable<CaseRow>
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        sortable
        paginated
        pagination={{ page: 0, pageSize: 10, pageSizeOptions: [10, 25, 50] }}
        density="compact"
        variant="glass"
        onRowClick={onNavigate ? (row) => onNavigate(`/loans/${row.id}`) : undefined}
      />
    </Box>
  );
};

export default CasesTable;
